import { Status, WebCatalogProduct, WebCatalogProductResponse } from 'utils/services/VervAPI';

export const pricePerDay = (props: {
	price?: number;
	days?: number;
	calculateFromIntro?: boolean;
}): number => {
	const { price, days, calculateFromIntro } = props;
	if (!price || !days) return 0;
	if (days <= 0) return 0;
	if (calculateFromIntro) return price / 7;
	return Math.round((price / days) * 100) / 100 || price;
};

export const pricePerDayV2 = (props: {
	price?: number;
	days?: number;
	calculateFromIntro?: boolean;
}): number => {
	const { price, days } = props;
	if (!price || !days) return 0;
	if (days <= 0) return 0;
	return Math.round((price / days) * 100) / 100 || price;
};

export const pricePerWeek = (props: {
	price: number;
	days: number;
	calculateFromIntro?: boolean;
}): number => {
	if (props.days <= 0) return 0;
	if (props.calculateFromIntro) return props.price;
	const daysCount = props.days > 365 ? 365 : props.days;
	return Math.round((props.price / daysCount) * 7 * 100) / 100 || props.price;
};

export const pricePerMounth = (props: {
	price?: number;
	mounths?: number;
	calculateFromIntro?: boolean;
}): number => {
	const { price, mounths, calculateFromIntro } = props;
	if (!price || !mounths) return 0;
	if (mounths <= 0) return 0;
	if (calculateFromIntro) return price;
	return Math.round((price / mounths) * 100) / 100;
};

export const pricePerMounthV2 = (props: {
	price?: number;
	mounths?: number;
	calculateFromIntro?: boolean;
}): number => {
	const { price, mounths } = props;
	if (!price || !mounths) return 0;
	if (mounths <= 0) return 0;
	return Math.round((price / mounths) * 100) / 100;
};

export const productPrice = (props: {
	product?: WebCatalogProductResponse | null;
	defaultPrice?: number;
	calculateFromIntro?: boolean;
}): number => {
	if (props.product == null || props.product.status !== Status.success)
		return props.defaultPrice ?? 0;

	return props.calculateFromIntro && props.product.data.intro_amount
		? props.product.data.intro_amount
		: props.product.data.amount;
};

export const productPriceV2 = (props: {
	product?: WebCatalogProductResponse | null;
	defaultPrice?: number;
	calculateFromIntro?: boolean;
}): number => {
	if (props.product == null || props.product.status !== Status.success)
		return props.defaultPrice ?? 0;

	if (props.calculateFromIntro && props.product.data.intro_amount) {
		if (props.product?.data.trial_period_days === 7) {
			return Math.round(props.product.data.intro_amount * 4.285 * 100) / 100;
		} else {
			return props.product.data.intro_amount;
		}
	} else {
		return props.product.data.amount;
	}
};

export const productPricePerDay = (props: {
	product: WebCatalogProductResponse | null;
	defaultPrice: number;
	calculateFromIntro?: boolean;
}): number => {
	if (props.product == null || props.product.status !== Status.success) return 0;
	return pricePerDay({
		price:
			props.calculateFromIntro && props.product.data.intro_amount
				? +props.product.data.intro_amount
				: +props.product.data.amount,
		days: daysIntervalFromProduct(props.product.data),
		calculateFromIntro: props.calculateFromIntro,
	});
};

export const productPricePerDayV2 = (props: {
	product: WebCatalogProductResponse | null;
	defaultPrice: number;
	calculateFromIntro?: boolean;
}): number => {
	if (props.product == null || props.product.status !== Status.success) return 0;
	return pricePerDayV2({
		price:
			props.calculateFromIntro && props.product.data.intro_amount
				? +props.product.data.intro_amount
				: +props.product.data.amount,
		days: daysIntervalFromProductV2(props.product.data),
		calculateFromIntro: props.calculateFromIntro,
	});
};

export const oldProductPricePerDay = (props: {
	product: WebCatalogProductResponse | null;
	defaultPrice: number;
	calculateFromIntro?: boolean;
	oldFixedPrice?: number;
}): number => {
	if (props.product == null || props.product.status !== Status.success) return 0;
	return pricePerDay({
		price: props.oldFixedPrice,
		days: daysIntervalFromProduct(props.product.data),
		calculateFromIntro: props.calculateFromIntro,
	});
};

export const oldProductPricePerDayWithFixedOldPrice = (props: {
	product: WebCatalogProductResponse | null;
	defaultPrice: number;
	calculateFromIntro?: boolean;
	oldPrice?: number;
}): number => {
	if (props.product == null || props.product.status !== Status.success) return 0;
	return pricePerDay({
		price: props.oldPrice,
		days: daysIntervalFromProduct(props.product.data),
		calculateFromIntro: props.calculateFromIntro,
	});
};

export const productPricePerWeek = (props: {
	product: WebCatalogProductResponse | null;
	defaultPrice?: number;
	calculateFromIntro?: boolean;
}): number => {
	if (props.product == null || props.product.status !== Status.success) return 0;

	return pricePerWeek({
		price:
			props.calculateFromIntro && props.product.data.intro_amount
				? +props.product.data.intro_amount
				: +props.product.data.amount,
		days: daysIntervalFromProduct(props.product.data),
		calculateFromIntro: props.calculateFromIntro,
	});
};

export const productPricePerMounth = (props: {
	product: WebCatalogProductResponse | null;
	defaultPrice?: number;
	calculateFromIntro?: boolean;
}): number => {
	if (props.product == null || props.product.status !== Status.success) return 0;

	return pricePerMounth({
		price:
			props.calculateFromIntro && props.product.data.intro_amount
				? +props.product.data.intro_amount
				: +props.product.data.amount,
		mounths: monthInterval(props.product.data),
		calculateFromIntro: props.calculateFromIntro,
	});
};

export const productPricePerMounthV2 = (props: {
	product: WebCatalogProductResponse | null;
	defaultPrice?: number;
	calculateFromIntro?: boolean;
}): number => {
	if (props.product == null || props.product.status !== Status.success) return 0;

	return pricePerMounthV2({
		price:
			props.calculateFromIntro && props.product.data.intro_amount
				? +props.product.data.intro_amount
				: +props.product.data.amount,
		mounths: monthInterval(props.product.data),
		calculateFromIntro: props.calculateFromIntro,
	});
};

export const daysIntervalFromProduct = (product: WebCatalogProduct): number => {
	if (!product.duration) return 0;

	const unitValue: number = product.duration.value ?? 0;
	let unitDays = 1;

	switch (product.duration.unit) {
		case 'week': {
			unitDays = 7;
			break;
		}
		case 'month': {
			unitDays = 31;
			break;
		}
		case 'year': {
			unitDays = 365;
			break;
		}
	}

	return unitValue * unitDays;
};

export const daysIntervalFromProductV2 = (product: WebCatalogProduct): number => {
	if (!product.duration) return 0;

	const unitValue: number = product.duration.value ?? 0;
	let unitDays = 1;

	switch (product.duration.unit) {
		case 'week': {
			unitDays = 7;
			break;
		}
		case 'month': {
			unitDays = 30;
			break;
		}
		case 'year': {
			unitDays = 365;
			break;
		}
	}
	if (product.trial_period_days === 7) {
		unitDays = 7;
	}

	if (Number(product.duration.value) === 12) {
		return 365;
	} else {
		return unitValue * unitDays;
	}
};

export const monthInterval = (product: WebCatalogProduct): number => {
	if (!product.duration) return 0;

	const unitValue: number = product.duration.value ?? 0;

	return unitValue;
};

export const priceFirstPart = (price: number) => Math.trunc(price);
export const priceSecondPart = (price: number) => {
	const pricePart = +price.toString().split('.')[1];
	return isNaN(pricePart)
		? '00'
		: pricePart.toString().length < 2
		? pricePart.toString() + '0'
		: pricePart;
};
